import { useCallback, useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Reader, type ReaderTag } from '@/api/client';
import { EditIcon } from '@/components/ui/Icons';
import { useToast } from '@/contexts/ToastContext';
import {
  cancelReadersTableListQueries,
  findReaderInTableCaches,
  optimisticPatchReaderInTableCaches,
  patchReaderInTableCache,
  invalidateReaderCachesExceptTable,
  restoreReadersTableInfiniteQueries,
  snapshotReadersTableInfiniteQueries,
  READER_TAGS_CATALOG_QUERY_KEY,
  type ReadersTableInfiniteSnapshot,
} from '@/lib/readersCatalogQueryCache';
import { groupReaderTagsByGroup } from '@/lib/readerTagCatalogGrouping';
import { ContextMenuDivider } from '@/components/ui/ContextMenu/ContextMenuDivider';
import { ReaderCatalogReaderTagGroupCascadeRow } from '@/components/ReadersTable/ReaderCatalogReaderTagGroupCascadeRow';
import '@/components/ui/ContextMenu/ContextMenu.css';

type MutationCtx = { snapshot: ReadersTableInfiniteSnapshot };

export interface ReaderCatalogReaderTagsSubmenuPanelProps {
  reader: Reader;
  disabled?: boolean;
  /** Открывает модалку справочника (родитель уже закрывает корневое меню при необходимости) */
  onOpenCatalogModal: () => void;
}

/** Панель второго уровня: группы → подменю тегов; внизу — «Редактировать». */
export function ReaderCatalogReaderTagsSubmenuPanel({
  reader,
  disabled = false,
  onOpenCatalogModal,
}: ReaderCatalogReaderTagsSubmenuPanelProps) {
  const queryClient = useQueryClient();
  const { showError } = useToast();

  const tagsQuery = useQuery({
    queryKey: READER_TAGS_CATALOG_QUERY_KEY,
    queryFn: () => api.readerTags.list(),
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation<Reader, Error, string[], MutationCtx>({
    mutationFn: (tag_ids) => api.readers.update(reader.id, { tag_ids }),
    onMutate: async (tag_ids) => {
      await cancelReadersTableListQueries(queryClient);
      const snapshot = snapshotReadersTableInfiniteQueries(queryClient);
      const catalog =
        queryClient.getQueryData<ReaderTag[]>(READER_TAGS_CATALOG_QUERY_KEY) ?? tagsQuery.data ?? [];
      const tagById = new Map(reader.tags?.map((t) => [t.id, t] as const) ?? []);
      for (const t of catalog) {
        tagById.set(t.id, t);
      }
      const nextTags: ReaderTag[] = [];
      for (const id of tag_ids) {
        const t = tagById.get(id);
        if (t) nextTags.push(t);
      }
      optimisticPatchReaderInTableCaches(queryClient, reader.id, { tags: nextTags });
      return { snapshot };
    },
    onSuccess: async (updated) => {
      patchReaderInTableCache(queryClient, updated);
      await invalidateReaderCachesExceptTable(queryClient);
    },
    onError: (e, _vars, ctx) => {
      if (ctx?.snapshot) {
        restoreReadersTableInfiniteQueries(queryClient, ctx.snapshot);
      }
      showError(e.message);
    },
  });

  const applyToggle = useCallback(
    (tagId: string, on: boolean) => {
      if (disabled || mutation.isPending) return;
      const fresh = findReaderInTableCaches(queryClient, reader.id) ?? reader;
      const base = fresh?.tags ?? reader.tags ?? [];
      const next = new Set(base.map((t) => t.id));
      if (on) next.add(tagId);
      else next.delete(tagId);
      mutation.mutate(Array.from(next));
    },
    [mutation, queryClient, reader.id, reader.tags, disabled]
  );

  const catalog = tagsQuery.data ?? [];
  const assigned = useMemo(() => new Set((reader.tags ?? []).map((t) => t.id)), [reader.tags]);
  const grouped = useMemo(() => groupReaderTagsByGroup(catalog), [catalog]);

  return (
    <div className="reader-catalog-tags-submenu-panel">
      {tagsQuery.isLoading && catalog.length === 0 ? (
        <p className="reader-catalog-tags-submenu-panel__status">Загрузка справочника…</p>
      ) : catalog.length === 0 ? (
        <p className="reader-catalog-tags-submenu-panel__status">Тегов в справочнике пока нет.</p>
      ) : (
        <ul className="reader-catalog-tags-submenu-panel__list reader-catalog-tags-submenu-panel__group-root" role="list">
          {grouped.map(({ group, tags }) => (
            <ReaderCatalogReaderTagGroupCascadeRow
              key={group}
              groupLabel={group}
              tags={tags}
              assignedIds={assigned}
              disabled={disabled}
              onToggleTag={applyToggle}
            />
          ))}
        </ul>
      )}
      <ContextMenuDivider listSpacing />
      <div className="reader-catalog-tags-submenu-panel__footer">
        <button
          type="button"
          className="reader-catalog-tags-submenu-panel__footer-action context-menu__action"
          onClick={() => onOpenCatalogModal()}
          disabled={disabled}
          role="menuitem"
        >
          <span className="reader-catalog-tags-submenu-panel__footer-action-icon" aria-hidden>
            <EditIcon size={18} />
          </span>
          <span className="reader-catalog-tags-submenu-panel__footer-action-text">Редактировать</span>
        </button>
      </div>
    </div>
  );
}
